import { all, call, select, takeLatest } from 'redux-saga/effects'
import { getUserSelector } from '../../user/selectors'
import { IUserState } from '../../user/store/user.types'
import { PcrDocsService } from '../../../services/pcrDocs.service'
import Logger from '../../../services/logger.service'
import { IPcr, IPcrs } from '../../pcr/store/pcr.types'
import { getVersion } from '../../../utilities'
import { SubmitChartService } from '../../submitChart/services/submitChart.service'
import { HomeConstants } from '../store/home.constants'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'
import { fetchPridSaga } from '../../submitChart/saga/preSubimtChart.saga'
import { doSubmitChartSaga } from '../../submitChart/saga/submitChart.saga'
import { IPcrSummaries } from '../store/home.types'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'
import { isValidZollNowDate } from '../../../utilities/dateTime.util'

// postSubmitChart
export function* callAutoUploadPostSubmitChart(pcrDoc: IPcr, submitChart: any) {
  try {
    const { serviceId }: IUserState = yield select(getUserSelector)
    const pcrDocsService = new PcrDocsService(serviceId)
    const { success, prid, emcUrl, responseMessage, chartLockIntended, nemsisGenerationSuccessful } = submitChart
    const { pcrId, createdBy } = pcrDoc
    // delete PCR after successful upload
    if (success && prid) {
      Logger.sensitive(`API Success: ${emcUrl}`, `API Success for PCR: ${pcrId}`)
      Logger.info(
        `PCR: [${pcrId}], chartLockIntended: [${chartLockIntended}], nemsisGenerationSuccessful: [${nemsisGenerationSuccessful}]`
      )

      yield call([pcrDocsService, pcrDocsService.deletePcrDoc], pcrId, createdBy)
      Logger.info(`PCR: ${pcrId} deleted after auto upload createdBy user: ${createdBy} in service: ${serviceId}`)
    } else {
      pcrDoc.submitChart = {
        ...pcrDoc.submitChart,
        ...submitChart,
        inProgress: false,
        lastAutoUploadDateTime: new Date().toISOString(),
      }
      if (pcrDoc.isChartLockedIn) {
        pcrDoc.isChartLockedIn = 0
        pcrDoc.completeChart = { ...pcrDoc.completeChart, lockChart: 0 }
        Logger.error(`Unlocking failed Chart [pcrId]: ${pcrDoc.pcrId}`)
      }
      yield call([pcrDocsService, pcrDocsService.savePCR], pcrDoc)
      Logger.error(`API Error: ${responseMessage}`)
    }
  } catch (error) {
    Logger.error(`Error callAutoUploadPostSubmitChart - Reason: ${error}`)
  }
}

// doSubmitChart
export function* callAutoUploadDoSubmitChart(pcrDoc: IPcr) {
  try {
    const { pcrId } = pcrDoc
    const user: IUserState = yield select(getUserSelector)
    const { authState } = user
    try {
      if (!pcrId) {
        const errorMsg = 'pcrID / pcrDocumentName does not exist'
        Logger.error(errorMsg)
        throw Error(errorMsg)
      }

      if (!authState) {
        const errorMsg = 'authState does not exist'
        Logger.error(errorMsg)
        throw Error(errorMsg)
      }

      yield call(doSubmitChartSaga, pcrDoc, false)
    } catch (e: unknown) {
      const cause = e instanceof Error ? e.message : 'unknown'
      const response = SubmitChartService.createErrorResponse(
        `An error occurred during chart submission. Cause: ${cause}`
      )
      yield call(callAutoUploadPostSubmitChart, pcrDoc, response)
    }
  } catch (e) {
    Logger.error(`Error inside callAutoUploadDoSubmitChart - Reason: ${e}`)
  }
}

function getEligibleCharts(pcrDocs: IPcrSummaries, userId: string, displaySeconds: boolean) {
  const currentVersion = getVersion()
  const autoUploadTime = Date.now() - (Number(process.env.REACT_APP_AUTO_UPLOAD_PCRS_MINUTES) || 720) * 60 * 1000 // timestamp in milliseconds either 12 hours ago or defined in REACT_APP_AUTO_UPLOAD_PCRS_MINUTES env variable
  const timeRegex = displaySeconds
    ? /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/ // HH:MM:SS (seconds required)
    : /^([0-1]?[0-9]|2[0-3]):[0-5][0-9](?::[0-5][0-9])?$/ // HH:MM (optional seconds)

  // filteredCharts
  return pcrDocs?.filter((pcr) => {
    const isCreatedByDifferentUser = pcr.createdBy !== userId // get charts of users except of logged in
    const referenceTime = pcr?.lastAutoUploadDateTime
      ? new Date(pcr.lastAutoUploadDateTime).getTime()
      : new Date(pcr.createDateTime).getTime()
    const isOlderThan12Hours = referenceTime < autoUploadTime // PCR should be older than 12 hours
    const hasValidDispatchedTime =
      Boolean(pcr.time?.trim()) &&
      Boolean(pcr.date?.trim()) &&
      timeRegex.test(pcr.time.trim()) &&
      isValidZollNowDate(pcr.date) // should have Dispatched date time associated with charts
    const isOfCurrentVersion = pcr.version === currentVersion // get current version's charts only
    const hasBaseAndUnit = Boolean(pcr.base?.trim()) && Boolean(pcr.unit?.trim())
    return (
      isCreatedByDifferentUser && isOlderThan12Hours && hasValidDispatchedTime && isOfCurrentVersion && hasBaseAndUnit
    )
  })
}

export function* autoUploadPCRs() {
  try {
    const { serviceId, userId }: IUserState = yield select(getUserSelector)
    if (!serviceId || !userId) {
      return
    }
    const { DISPLAY_SECONDS } = yield select(getServiceConfigSelector)
    const displaySeconds = !!DISPLAY_SECONDS

    const pcrDocsService = new PcrDocsService(serviceId)
    const pcrDocs: IPcrSummaries = yield call([pcrDocsService, pcrDocsService.getAllPCRDocs])
    const filteredChartsForAutoUpload = getEligibleCharts(pcrDocs, userId, displaySeconds)

    //--------SUBMIT LOGIC-----------//
    if (filteredChartsForAutoUpload?.length) {
      Logger.info(`Number of PCRs eligible for auto-upload = ${filteredChartsForAutoUpload.length}`)
      Logger.info('Initiating auto upload PCRs')

      const fullPcrDocs: IPcrs = yield all(
        filteredChartsForAutoUpload.map((summary) => call([pcrDocsService, pcrDocsService.getPCR], summary.pcrId))
      )

      for (const pcrDoc of fullPcrDocs) {
        if (!pcrDoc.prid) {
          // fetch prid
          yield call(fetchPridSaga, pcrDoc, false)
        } else {
          yield call(callAutoUploadDoSubmitChart, pcrDoc)
        }
      }
    }
  } catch (e) {
    Logger.error(`Error inside autoUploadPCRsSaga - Reason: ${e}`)
  }
}

export function* autoUploadPCRsSaga() {
  yield takeLatest([HomeConstants.INITIATE_AUTO_UPLOAD], performWithMutexHOG, autoUploadPCRs)
}
